<template class="text-filter">
  <CardWithTitle
    :item-id="itemId"
    :request-status="requestStatus"
    @reload="resetFilter"
  >
    <template #top-right-slot>
      <DsButton
        v-tooltip="filterLabel"
        :color="color"
        name="filter"
        :icon="true"
        @click="openFilter"
      />
      <DsButton
        v-tooltip="infoLabel"
        color="tertiary"
        name="questions"
        :icon="true"
        @click="openDialog"
      />
    </template>
    <v-dialog
      v-model="filterDialog"
      width="70vw"
      height="80vh"
    >
      <v-card>
        <v-card-actions class="text-filter__close-button">
          <DsButton
            v-tooltip="closeLabel"
            color="tertiary"
            name="close"
            :icon="true"
            @click="filterDialog = false"
          />
        </v-card-actions>
        <div class="text-filter__internal-style">
          <v-text-field
            v-model="textQuery"
            v-tooltip="$t('components.TextFilter.searchTip')"
            class="mr-3"
            :rules="rules.minChar"
            prepend-icon="mdi-text-search"
            :label="$t('components.TextFilter.label.search')"
            @keyup.enter="getData"
          />
          <MultiSelectFilter
            v-model="selectedTags"
            class="mr-3"
            :select-items="setTags"
            :label="$t('components.TextFilter.label.tags')"
            :searchable="true"
          />
          <div class="text-filter__buttons">
            <DsButton
              v-tooltip="$t('components.TextFilter.label.filterLabel')"
              class="ma-2 text-filter__button"
              color="secondary"
              name="filter"
              :icon="true"
              @click="getData"
            />
            <br>
            <DsButton
              v-tooltip="$t('components.TextFilter.label.trashLabel')"
              class="ma-2 text-filter__button"
              color="secondary"
              name="trash"
              :icon="true"
              @click="resetFilter"
            />
          </div>
        </div>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="dialog"
      width="70vw"
      height="80vh"
    >
      <v-card>
        <v-card-actions
          class="text-filter__close-button"
        >
          <DsButton
            v-tooltip="closeLabel"
            #top-right
            color="tertiary"
            name="close"
            :icon="true"
            @click="dialog = false"
          />
        </v-card-actions>
        <v-card-text class="text-filter__modal-style">
          <h3>{{ this.$t('components.TextFilter.faq.title') }}</h3>
          <p>{{ this.$t('components.TextFilter.faq.description') }}</p>

          <h3>{{ this.$t('components.TextFilter.faq2.title') }}</h3>
          <p>{{ this.$t('components.TextFilter.faq2.description') }}</p>

          <h3>{{ this.$t('components.TextFilter.faq3.title') }}</h3>
          <p>{{ this.$t('components.TextFilter.faq3.description') }}</p>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="postDialog"
      fullscreen
    >
      <v-card>
        <v-toolbar>
          <v-toolbar-title>{{ $t('components.OperatorsLogs.details') }}</v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <StIcon
              class="close-icon"
              icon-name="close"
              @click="closeCard"
            />
          </v-toolbar-items>
        </v-toolbar>
        <v-card-actions>
          <PostThemeAndSentiment :post="postCard" />
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-card
      v-for="post in rawResults"
      :key="post._id"
      outlined
    >
      <v-card-text
        class="card-content"
      >
        <v-list-item
          class="text-filter__card-title"
        >
          {{ getId(post.text) }}
          <v-card-actions>
            <CopyToClipboard
              :content="urlWithoutProtocol(post.post_url)"
            />
            <DsButton
              v-tooltip="viewLabel"
              class="text-filter__clean-sla-buttons"
              color="secondary"
              name="view"
              :icon="true"
              @click="openCard({ pid: post.pid })"
            />
          </v-card-actions>
        </v-list-item>
        <v-list-item>
          <div>
            <span
              class="font-weight-bold"
            >
              {{ $t('components.TextFilter.titles.postedAt') }}
            </span>
            {{ formatDate(post.posted_at) }}
          </div>
        </v-list-item>
        <v-list-item>
          <div>
            <span
              class="font-weight-bold"
            >
              {{ $t('components.TextFilter.titles.tags') }}
            </span>
            {{ getTags(post.tags) }}
          </div>
        </v-list-item>
        <v-list-item>
          <div>
            <span
              class="font-weight-bold"
            >
              {{ $t('components.TextFilter.titles.text') }}
            </span>
            {{ generateSampleText(post.text) }}
          </div>
        </v-list-item>
      </v-card-text>
    </v-card>
    <h2
      v-if="!hasPost && !error"
      class="no-post-title pt-16"
    >
      {{ noPostsLabel }}
    </h2>
    <h2
      v-if="error"
      class="no-post-title pt-16"
    >
      {{ errorLabel }}
    </h2>
  </CardWithTitle>
</template>

<script>
import CardWithTitle from '@/components/cards/models/CardWithTitle';
import { DsButton } from '@ds/components';
import { mapActions, mapGetters, mapState } from 'vuex';
import MultiSelectFilter from '@/components/filter/MultiSelectFilter.vue';
import StIcon from '@stilingue/st-icon';
import WarRoomService from '@/services/WarRoomService';
import PostThemeAndSentiment from '@/components/Post/PostThemeAndSentiment';
import { STANDARD_OLD_TIME_STAMP } from '@/utils/date';
import CopyToClipboard from '@/components/CopyToClipboard';

export default {
  name: 'TextSearch',
  components: {
    CardWithTitle,
    DsButton,
    MultiSelectFilter,
    StIcon,
    PostThemeAndSentiment,
    CopyToClipboard,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      textQuery: null,
      selectedTags: null,
      rawResults: [],
      error: false,
      success: false,
      dialog: false,
      filterDialog: false,
      infoLabel: this.$t('components.TextFilter.infoLabel'),
      filterLabel: this.$t('components.TextFilter.filter'),
      closeLabel: this.$t('components.TextFilter.closeLabel'),
      noPostsLabel: this.$t('components.TextFilter.noPosts'),
      viewLabel: this.$t('components.TextFilter.viewLabel'),
      dateTitle: `${this.$t('components.TextFilter.titles.postedAt')} `,
      errorLabel: this.$t('components.TextFilter.errorLabel'),
      viewPost: this.$t('components.OperatorsLogs.viewPost'),
      postDialog: false,
      requestStatus: 'success',
      valid: true,
      rules: {
        minChar: [v => (v && v.length >= 2) || 'Deve conter pelo menos dois caracteres'],
      },
      postCard: null,
    };
  },
  computed: {
    ...mapGetters('dashboard', [
      'activeDashboardFilters',
    ]),
    ...mapGetters('filter', [
      'formattedDateRange',
      'getEndDate',
    ]),
    ...mapState('filter', [
      'accountId',
      'universeId',
    ]),
    ...mapState('dashboard', [
      'isStaging',
    ]),
    hasPost() {
      return this.rawResults && this.rawResults.length;
    },
    setTags() {
      return this.activeDashboardFilters?.tags || [];
    },
    color() {
      if (this.rawResults.length) {
        return 'primary';
      }
      return 'tertiary';
    },
    tagSelection() {
      if (this.selectedTags) {
        return this.activeDashboardFilters.tags.length === (Object.values(this.selectedTags)[0]).length ? null : this.selectedTags;
      }
      return this.selectedTags;
    },
  },

  methods: {
    ...mapActions('cards', [
      'getWarroomService',
    ]),
    openDialog() {
      this.dialog = true;
    },
    openFilter() {
      this.filterDialog = true;
    },
    async getData() {
      this.requestStatus = 'loading';
      this.rawResults = [];
      const defaultParams = {
        date_range: this.formattedDateRange,
        account_id: this.accountId,
        universe_id: this.universeId,
        channel: 'importedData',
        limit: 1000,
        filter: {
          query: {
            value: this.textQuery,
          },
          tags: this.tagSelection,
          is_sac: false,
        },
      };
      WarRoomService.topPosts({ params: defaultParams })
        .then(response => {
          this.rawResults = response.posts;
          this.error = !this.rawResults.length;
          this.requestStatus = 'success';
        })
        .catch((err) => {
          console.error(err);
          this.requestStatus = 'error';
        });
      this.loading = false;
      this.filterDialog = false;
    },
    resetFilter() {
      this.textQuery = null;
      this.selectedTags = null;
      this.rawResults = [];
      this.error = false;
    },
    urlWithoutProtocol(url) {
      return url.substring(7);
    },
    removeHTMLtags(text) {
      return text.replace(/<[^>]+>/giu, '');
    },
    generateSampleText(text) {
      const newText = this.generateText(text);
      return `${newText.substring(0, 299)}...`;
    },
    generateText(text) {
      const newText = this.removeHTMLtags(text);
      return newText;
    },
    getTags(tags) {
      return tags.join(', ');
    },
    formatDate(date) {
      return date.substring(0, 11);
    },
    getId(text) {
      const id = text.replace(/<\/?u>/giu, '').replace('.pdf', '').split('<br>')[0];
      return this.removeHTMLtags(id);
    },
    async openCard({ pid, sacCallId }) {
      this.$attrs.filter = { pids: [pid] };
      this.$attrs.filterId = null;
      const defaultParams = {
        date_range: `${STANDARD_OLD_TIME_STAMP}:${this.getEndDate}`,
        account_id: this.accountId,
        universe_id: this.universeId,
        is_staging: this.isStaging,
        channel: 'importedData',
        filter: {
          is_sac: false,
        },
      };
      pid ? (defaultParams.filter.pids = { value: [pid] }) : (defaultParams.filter.sac_call_ids = { value: [sacCallId] });
      WarRoomService.topPosts({ params: defaultParams })
        .then(res => {
          this.postDialog = true;
          this.postCard = res.posts[0];
        }).catch(err => console.error(err));
    },
    closeCard() {
      this.postDialog = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.text-filter {
  &__internal-style {
    width: 100%;
    padding: 1rem;
    display: flex;
  }

  &__buttons {
    display: flex;
  }

  &__button {
    box-shadow: none;
    border-radius: unset;
  }

  &__close-button {
    display: flex;
    flex: end;
    justify-content: flex-end;
  }

  &__card-title {
    display: flex;
    align-items: center;
    font-weight: bold;
    gap: 0.5rem;
  }

  &__modal-style {
    * {
      color: var(--ds-color-snow) !important;
    }
  }

  &__clean-sla-buttons {
    box-shadow: none;
    border-radius: unset;
  }
}

</style>

